import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import myAxios from "../AxiosUrl";
import styles from "./TournamentSquads.module.css";
import Loading from "../Loading/Loading";

const TournamentSquads = () => {
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSquads, setTotalSquads] = useState(0);

  // Statistics
  const [stats, setStats] = useState({
    totalCompetitions: 0,
    totalPlayers: 0,
    totalTeams: 0,
  });

  // Filters
  const [selectedCompetition, setSelectedCompetition] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [competitions, setCompetitions] = useState([]);
  const [teams, setTeams] = useState([]);

  // Modal state
  const [selectedSquad, setSelectedSquad] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [squadToDelete, setSquadToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchSquads();
  }, [
    currentPage,
    searchTerm,
    selectedCompetition,
    selectedTeam,
    selectedStatus,
  ]);

  const fetchSquads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        competitionName: selectedCompetition,
        contestantName: selectedTeam,
        status: selectedStatus,
      });

      const response = await myAxios.get(`/tournament/squads?${params}`);
      const data = response.data;

      setSquads(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalSquads(data.pagination?.totalSquads || 0);

      // Update statistics from the main response
      setStats({
        totalCompetitions: data.totalCompetitions || 0,
        totalPlayers: data.totalPlayers || 0,
        totalTeams: data.totalTeams || 0,
      });

      // Extract unique teams and competitions
      const uniqueTeams = [
        ...new Set(data.data?.map((squad) => squad.contestantName) || []),
      ];
      setTeams(uniqueTeams);

      // Extract unique competition names first
      const uniqueCompetitionNames = [
        ...new Set(data.data?.map((squad) => squad.competitionName) || [])
      ];

      // Then create objects with unique IDs
      const uniqueCompetitions = uniqueCompetitionNames.map((name, index) => ({
        id: `comp-${index}-${name}`, // Create unique ID
        name: name,
      }));
      setCompetitions(uniqueCompetitions);
    } catch (err) {
      setError("Error fetching tournament squads");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSquadClick = async (squadId) => {
    try {
      const response = await myAxios.get(`/tournament/squads/${squadId}`);
      const data = response.data;
      setSelectedSquad(data.data);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching squad details:", err);
    }
  };

const handleDeleteClick = (squad) => {
    setSquadToDelete(squad);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!squadToDelete) return;

    try {
      setDeleting(true);
      const response = await myAxios.delete(
        `/tournament/squads/${squadToDelete.contestantId}`
      );

      if (response.data.success) {
        // Refresh the squads data
        fetchSquads();
        alert("Squad deleted successfully!");
      }
    } catch (err) {
      console.error("Error deleting squad:", err);
      alert("Failed to delete squad. Please try again.");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setSquadToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setSquadToDelete(null);
  };

  const handleCleanupNonTslData = async () => {
    const confirmed = window.confirm(
      "This will permanently remove tournament data outside the Turkish Super League from the admin dataset. Continue?"
    );

    if (!confirmed) {
      return;
    }

    try {
      setIsCleaningUp(true);
      const response = await myAxios.post(
        "/tournament/cleanup/scopes/turkish-super-lig"
      );

      if (response.data?.success) {
        const stats = response.data.stats || {};
        await fetchSquads();
        setCurrentPage(1);
        alert(
          `Cleanup completed.\n\nRemoved players: ${stats.playersRemoved || 0}\nRemoved squads: ${stats.squadsRemoved || 0}\nRemoved fixtures: ${stats.fixturesRemoved || 0}\nRemoved schedule rows: ${stats.scheduleRemoved || 0}\nRemoved standings docs: ${stats.standingsRemoved || 0}`
        );
      }
    } catch (err) {
      console.error("Error cleaning tournament data:", err);
      const message =
        err?.response?.data?.message ||
        "Failed to clean non-Turkish Super League tournament data.";
      alert(message);
    } finally {
      setIsCleaningUp(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSelectedCompetition("");
    setSelectedTeam("");
    setSelectedStatus("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const getPositionColor = (position) => {
    const colors = {
      Goalkeeper: "#28a745",
      Defender: "#007bff",
      Midfielder: "#ffc107",
      Forward: "#dc3545",
    };
    return colors[position] || "#6c757d";
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className={styles.paginationBtn}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key="start-ellipsis" className={styles.paginationEllipsis}>
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`${styles.paginationBtn} ${currentPage === i ? styles.active : ""}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="end-ellipsis" className={styles.paginationEllipsis}>
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={styles.paginationBtn}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className={styles.tournamentSquadsContainer}>
        <div className={styles.tournamentSquadsContent}>
          <div className={styles.errorMessage}>
            <span className="material-icons">error</span>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tournamentSquadsContainer}>
      <div className={styles.tournamentSquadsContent}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <div className={styles.pageHeaderRow}>
            <div>
              <h1 className={styles.pageTitle}>Tournament Squads</h1>
              <p className={styles.pageSubtitle}>Manage tournament squads and players</p>
            </div>
            <div className={styles.headerActions}>
              <button
                type="button"
                onClick={handleCleanupNonTslData}
                className={styles.cleanupButton}
                disabled={isCleaningUp}
              >
                <span className="material-icons">
                  {isCleaningUp ? "hourglass_top" : "cleaning_services"}
                </span>
                {isCleaningUp ? "Cleaning..." : "Keep TSL Only"}
              </button>
              <p className={styles.cleanupHint}>
                Removes non-Turkish Super League tournament data from admin collections.
              </p>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className={styles.statsSection}>
          <motion.div
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className={`${styles.statIcon} ${styles.competitions}`}>
              <span className="material-icons">sports_soccer</span>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.totalCompetitions}</div>
              <div className={styles.statLabel}>Total Competitions</div>
            </div>
          </motion.div>

          <motion.div
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className={`${styles.statIcon} ${styles.teams}`}>
              <span className="material-icons">groups</span>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.totalTeams}</div>
              <div className={styles.statLabel}>Total Teams</div>
            </div>
          </motion.div>

          <motion.div
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <div className={`${styles.statIcon} ${styles.players}`}>
              <span className="material-icons">person</span>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.totalPlayers}</div>
              <div className={styles.statLabel}>Total Players</div>
            </div>
          </motion.div>
        </div>

        {/* Compact Filters Section */}
        <div className={styles.compactFiltersSection}>
          <div className={styles.filtersRow}>
            {/* Search Input */}
            <div className={styles.compactSearch}>
              <div className={styles.searchInputWrapper}>
                <span className={`material-icons ${styles.searchIcon}`}>search</span>
                <input
                  type="text"
                  placeholder="Search squads..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className={styles.compactSearchInput}
                />
              </div>
            </div>

            {/* Filter Dropdowns */}
            <div className={styles.compactFilters}>
              <div className={styles.filterDropdown}>
                <select
                  value={selectedCompetition}
                  onChange={(e) => {
                    setSelectedCompetition(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.compactSelect}
                >
                  <option value="">All Competitions</option>
                  {competitions.map((comp) => (
                    <option key={comp.id} value={comp.name}>
                      {comp.name}
                    </option>
                  ))}
                </select>
                <span className={`material-icons ${styles.selectArrow}`}>expand_more</span>
              </div>

              <div className={styles.filterDropdown}>
                <select
                  value={selectedTeam}
                  onChange={(e) => {
                    setSelectedTeam(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.compactSelect}
                >
                  <option value="">All Teams</option>
                  {teams.map((team) => (
                    <option key={team} value={team}>
                      {team}
                    </option>
                  ))}
                </select>
                <span className={`material-icons ${styles.selectArrow}`}>expand_more</span>
              </div>

              <div className={styles.filterDropdown}>
                <select
                  value={selectedStatus}
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.compactSelect}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <span className={`material-icons ${styles.selectArrow}`}>expand_more</span>
              </div>
            </div>

            {/* Clear Filters Button */}
            {(selectedCompetition ||
              selectedTeam ||
              selectedStatus ||
              searchTerm) && (
              <button
                onClick={clearFilters}
                className={styles.compactClearBtn}
                title="Clear all filters"
              >
                <span className="material-icons">clear</span>
              </button>
            )}
          </div>

          {/* Active Filters Display */}
          {(selectedCompetition || selectedTeam || selectedStatus) && (
            <div className={styles.activeFilters}>
              {selectedCompetition && (
                <span className={styles.filterTag}>
                  Competition: {selectedCompetition}
                  <span
                    className={`material-icons ${styles.filterTagRemove}`}
                    onClick={() => {
                      setSelectedCompetition("");
                      setCurrentPage(1);
                    }}
                  >
                    close
                  </span>
                </span>
              )}
              {selectedTeam && (
                <span className={styles.filterTag}>
                  Team: {selectedTeam}
                  <span
                    className={`material-icons ${styles.filterTagRemove}`}
                    onClick={() => {
                      setSelectedTeam("");
                      setCurrentPage(1);
                    }}
                  >
                    close
                  </span>
                </span>
              )}
              {selectedStatus && (
                <span className={styles.filterTag}>
                  Status: {selectedStatus}
                  <span
                    className={`material-icons ${styles.filterTagRemove}`}
                    onClick={() => {
                      setSelectedStatus("");
                      setCurrentPage(1);
                    }}
                  >
                    close
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        <div className={styles.tableContainer}>
          <table className={styles.squadsTable}>
            <thead>
              <tr>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>group</span>
                  Team
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>
                    sports_soccer
                  </span>
                  Competition
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>business</span>
                  Code
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>person</span>
                  Total Players
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>
                    check_circle
                  </span>
                  Active Players
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>settings</span>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {squads.map((squad) => (
                <motion.tr
                  key={squad.contestantId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={styles.tableRow}
                >
                  <td className={styles.squadCell}>
                    <div className={styles.squadInfo}>
                      <div className={styles.squadAvatar}>
                        {squad.teamImage ? (
                          <img
                            src={squad.teamImage}
                            alt={squad.contestantName}
                            className={styles.teamImage}
                          />
                        ) : (
                          <span className="material-icons">shield</span>
                        )}
                      </div>
                      <div className={styles.squadDetails}>
                        <span className={styles.squadName}>
                          {squad.contestantName}
                        </span>
                        <span className={styles.squadId}>
                          ID: {squad.contestantId?.slice(-6)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.competitionBadge}>
                      {squad.competitionName || "N/A"}
                    </div>
                  </td>
                  <td>
                    <div className={styles.teamName}>
                      <span className={`material-icons ${styles.teamIcon}`}>
                        sports_soccer
                      </span>
                      {squad.contestantCode || squad.contestantName}
                    </div>
                  </td>
                  <td>
                    <div className={styles.playersCount}>
                      <span className={styles.countBadge}>
                        {squad.totalPlayers || 0}
                      </span>
                      <span className={styles.countText}>players</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.pointsDisplay}>
                      <span className={`material-icons ${styles.starIcon}`}>star</span>
                      <span className={styles.pointsValue}>
                        {squad.activePlayers || 0}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.actionsGroup}>
                      <button
                        onClick={() => handleSquadClick(squad.contestantId)}
                        className={`${styles.actionBtn} ${styles.viewBtn}`}
                        title="View Details"
                      >
                        <span className="material-icons">visibility</span>
                      </button>

                      <button
                        onClick={() => handleDeleteClick(squad)}
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        title="Delete Squad"
                      >
                        <span className="material-icons">delete</span>
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>

          {squads.length === 0 && (
            <div className={styles.emptyState}>
              <span className={`material-icons ${styles.emptyIcon}`}>search_off</span>
              <h3>No squads found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.paginationContainer}>
            <div className={styles.paginationInfo}>
              <span className="material-icons">info</span>
              Total: <strong>{totalSquads}</strong> squads
            </div>
            <div className={styles.paginationControls}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`${styles.paginationBtn} ${styles.navBtn}`}
              >
                <span className="material-icons">chevron_left</span>
              </button>
              {renderPagination()}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`${styles.paginationBtn} ${styles.navBtn}`}
              >
                <span className="material-icons">chevron_right</span>
              </button>
            </div>
            <div className={styles.paginationInfo}>
              Page <strong>{currentPage}</strong> of{" "}
              <strong>{totalPages}</strong>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && squadToDelete && (
        <div className={styles.modalOverlay} onClick={handleDeleteCancel}>
          <div
            className={styles.deleteModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.deleteModalHeader}>
              <h3>Confirm Delete</h3>
              <span className={`material-icons ${styles.deleteIcon}`}>warning</span>
            </div>
            <div className={styles.deleteModalBody}>
              <p>Are you sure you want to delete this squad?</p>
              <div className={styles.squadDeleteInfo}>
                <strong>{squadToDelete.contestantName}</strong>
                <span>Competition: {squadToDelete.competitionName}</span>
                <span>Players: {squadToDelete.totalPlayers || 0}</span>
              </div>
              <p className={styles.deleteWarning}>This action cannot be undone.</p>
            </div>
            <div className={styles.deleteModalActions}>
              <button
                onClick={handleDeleteCancel}
                className={styles.btnCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className={styles.btnDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span className={`material-icons ${styles.rotating}`}>
                      hourglass_empty
                    </span>
                    Deleting...
                  </>
                ) : (
                  <>
                    <span className="material-icons">delete</span>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && selectedSquad && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{selectedSquad.contestantName || "Squad Details"}</h2>
              <button
                onClick={() => setShowModal(false)}
                className={styles.modalClose}
              >
                <span className="material-icons">close</span>
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* Team Image Section */}
              <div className={styles.teamImageSection}>
                <h3 className={styles.sectionTitle}>Team Image</h3>
                <div className={styles.teamImageUpload}>
                  <div className={styles.currentTeamImage}>
                    {selectedSquad.teamImage ? (
                      <img
                        src={selectedSquad.teamImage}
                        alt={selectedSquad.contestantName}
                        className={styles.teamLogoDisplay}
                      />
                    ) : (
                      <div className={styles.teamLogoPlaceholder}>
                        <span className="material-icons">shield</span>
                        <span>No team image</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.squadDetailsGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Competition:</span>
                  <span className={styles.detailValue}>
                    {selectedSquad.competitionName || "N/A"}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Team:</span>
                  <span className={styles.detailValue}>
                    {selectedSquad.contestantName || "N/A"}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Team Code:</span>
                  <span className={styles.detailValue}>
                    {selectedSquad.contestantCode || "N/A"}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Total Players:</span>
                  <span className={styles.detailValue}>
                    {selectedSquad.totalPlayers || 0}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Active Players:</span>
                  <span className={styles.detailValue}>
                    {selectedSquad.activePlayers || 0}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Tournament:</span>
                  <span className={styles.detailValue}>
                    {selectedSquad.tournamentName || "N/A"}
                  </span>
                </div>
              </div>

              {selectedSquad.players && selectedSquad.players.length > 0 && (
                <div className={styles.playersSection}>
                  <h3>Players ({selectedSquad.players.length})</h3>
                  <div className={styles.playersGrid}>
                    {selectedSquad.players.map((player, index) => (
                      <div
                        key={index}
                        className={styles.playerCard}
                        data-position={player.position}
                      >
                        <div className={styles.playerAvatar}>
                          {player.profileImage ? (
                            <img
                              src={player.profileImage}
                              alt={player.firstName || player.matchName}
                              className={styles.playerProfileImage}
                            />
                          ) : (
                            <span className="material-icons">person</span>
                          )}
                        </div>
                        <div className={styles.playerInfo}>
                          <span className={styles.playerName}>
                            {player.firstName && player.lastName
                              ? `${player.firstName} ${player.lastName}`
                              : player.matchName ||
                                player.firstName ||
                                player.lastName ||
                                "Unknown Player"}
                          </span>
                          <span
                            className={styles.playerPosition}
                            style={{ color: getPositionColor(player.position) }}
                          >
                            {player.position || "Unknown"}
                          </span>
                          {player.nationality && (
                            <span className={styles.playerNationality}>
                              {player.nationality}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Schedule Data Section */}
              {selectedSquad.scheduleData && (
                <div className={styles.scheduleSection}>
                  <h3>
                    Match Schedule ({selectedSquad.scheduleData.totalMatches}{" "}
                    matches)
                  </h3>

                  {/* Schedule Statistics */}
                  <div className={styles.scheduleStats}>
                    <div className={styles.scheduleStatCard}>
                      <div className={styles.scheduleStatIcon}>📅</div>
                      <div className={styles.scheduleStatContent}>
                        <div className={styles.scheduleStatNumber}>
                          {selectedSquad.scheduleData.totalMatches}
                        </div>
                        <div className={styles.scheduleStatLabel}>Total Matches</div>
                      </div>
                    </div>
                    <div className={styles.scheduleStatCard}>
                      <div className={styles.scheduleStatIcon}>📊</div>
                      <div className={styles.scheduleStatContent}>
                        <div className={styles.scheduleStatNumber}>
                          {selectedSquad.scheduleData.dateWiseCount}
                        </div>
                        <div className={styles.scheduleStatLabel}>Match Days</div>
                      </div>
                    </div>
                  </div>

                  {/* Date-wise Match List */}
                  {Object.keys(selectedSquad.scheduleData.dateWiseMatches)
                    .length > 0 && (
                    <div className={styles.dateWiseMatches}>
                      {Object.entries(
                        selectedSquad.scheduleData.dateWiseMatches
                      )
                        .sort(
                          ([dateA], [dateB]) =>
                            new Date(dateA) - new Date(dateB)
                        )
                        .map(([date, matches]) => (
                          <div key={date} className={styles.matchDateGroup}>
                            <div className={styles.matchDateHeader}>
                              <span className="material-icons">event</span>
                              <span className={styles.matchDateText}>
                                {new Date(date).toLocaleDateString("en-US", {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })}
                              </span>
                              <span className={styles.matchCountBadge}>
                                {matches.length}{" "}
                                {matches.length === 1 ? "match" : "matches"}
                              </span>
                            </div>

                            <div className={styles.matchesForDate}>
                              {matches.map((match, index) => (
                                <div
                                  key={match.matchId || index}
                                  className={styles.matchItem}
                                >
                                  <div className={styles.matchTime}>
                                    <span className="material-icons">
                                      schedule
                                    </span>
                                    <span>
                                      {match.time || match.localTime || "TBD"}
                                    </span>
                                  </div>

                                  <div className={styles.matchTeams}>
                                    <div
                                      className={`${styles.teamInMatch} ${match.isHome ? styles.currentTeam : ""}`}
                                    >
                                      {match.homeTeam.name}
                                      {match.homeTeam.code && (
                                        <span className={styles.teamCode}>
                                          ({match.homeTeam.code})
                                        </span>
                                      )}
                                    </div>

                                    <div className={styles.vsIndicator}>VS</div>

                                    <div
                                      className={`${styles.teamInMatch} ${!match.isHome ? styles.currentTeam : ""}`}
                                    >
                                      {match.awayTeam.name}
                                      {match.awayTeam.code && (
                                        <span className={styles.teamCode}>
                                          ({match.awayTeam.code})
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <div className={styles.matchDetails}>
                                    {match.venue && (
                                      <div className={styles.matchVenue}>
                                        <span className="material-icons">
                                          location_on
                                        </span>
                                        <span>{match.venue}</span>
                                      </div>
                                    )}

                                    {match.round && (
                                      <div className={styles.matchRound}>
                                        <span className="material-icons">
                                          sports_score
                                        </span>
                                        <span>Round {match.round}</span>
                                      </div>
                                    )}

                                    {match.status && (
                                      <div className={styles.matchStatus}>
                                        <span
                                          className={styles.statusIndicator}
                                          style={{
                                            backgroundColor:
                                              match.status === "live"
                                                ? "#28a745"
                                                : match.status === "completed"
                                                ? "#6c757d"
                                                : match.status === "upcoming"
                                                ? "#007bff"
                                                : "#ffc107",
                                          }}
                                        ></span>
                                        <span>{match.status}</span>
                                      </div>
                                    )}
                                  </div>

                                  <div className={styles.matchMeta}>
                                    <div className={styles.homeAwayIndicator}>
                                      {match.isHome ? (
                                        <span className={styles.homeIndicator}>
                                          🏠 Home
                                        </span>
                                      ) : (
                                        <span className={styles.awayIndicator}>
                                          ✈️ Away
                                        </span>
                                      )}
                                    </div>

                                    <div className={styles.opponentInfo}>
                                      vs <strong>{match.opponent}</strong>
                                      {match.opponentCode && (
                                        <span className={styles.opponentCode}>
                                          ({match.opponentCode})
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* No Schedule Data Message */}
                  {selectedSquad.scheduleData.totalMatches === 0 && (
                    <div className={styles.noScheduleData}>
                      <span className="material-icons">event_busy</span>
                      <p>No match schedule data available for this team</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentSquads;
